// Connecting a customer's Slack workspace to their Elliot organization.
//
// `/connect/slack` runs the Descope flow below (sign in + Slack approval), then
// tells elliot-server, which takes the organization and email from the session.

const SLACK_CONNECT_PATH = '/connect/slack';

/** Is this the connect page? A trailing slash (a pasted link) counts too. */
export function isSlackConnectPath(pathname: string): boolean {
  return pathname.replace(/\/$/, '') === SLACK_CONNECT_PATH;
}

// The Descope flow: sign in, then Tenant Connect to the `slack` Outbound App.
export const SLACK_CONNECT_FLOW_ID = 'slack-connect';

/** What to tell the person when the server refuses the connect. */
export function slackConnectErrorMessage(status: number): string {
  if (status === 409) return 'This Slack workspace is already connected to another Elliot organization.';
  if (status === 404) return "Slack wasn't approved. Please try again.";
  if (status === 401 || status === 403) return 'Please sign in with your own Elliot account to connect Slack.';
  return 'Something went wrong connecting Slack. Please try again.';
}

export type SlackConnectState = 'flow' | 'connecting' | { connected: string } | { error: string };

/**
 * What the page does around the flow, kept out of React so it can be tested:
 * `onSuccess` tells the server and reports the outcome; `retry` goes back to the
 * flow. A click landing while an attempt is still running is ignored.
 */
export function createSlackConnect(
  connect: () => Promise<{ slack_workspace_name: string }>,
  setState: (state: SlackConnectState) => void,
) {
  let busy = false;
  return {
    async onSuccess() {
      if (busy) return;
      busy = true;
      setState('connecting');
      try {
        const { slack_workspace_name } = await connect();
        setState({ connected: slack_workspace_name });
      } catch (e) {
        setState({ error: e instanceof Error ? e.message : slackConnectErrorMessage(0) });
        busy = false;
      }
    },
    retry() {
      setState('flow');
    },
  };
}
